import React from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import usePrecheck from '../../hooks/usePrecheck';
import { useTranslation } from 'react-i18next';
import SinglePrecheckView from '../shared/SinglePrecheckView';

const SinglePrecheck: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { precheck, loading, error } = usePrecheck(id);
  const { t } = useTranslation();

  const getTitle = (data: typeof precheck) => {
    return `${t('prechecks.title')} ${t('common.for')} ${data?.genInfo.genComp ?? '-'}`;
  };

  const renderContent = (data: NonNullable<typeof precheck>) => (
    <>
      <h3 className="text-md font-bold mb-2 mt-8">{t('prechecks.generalInfo')}</h3>

      <table className="table-auto w-full text-sm my-2">
        <tbody>
          <tr>
            <td className="border px-4 py-2">Region</td>
            <td className="border px-4 py-2 uppercase">{precheck?.reg}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">Services</td>
            <td className="border px-4 py-2">
              {precheck?.srv
                ? precheck.srv.map((service, index) => (
                  <a key={index} href={`#${service}`} className="mr-1">
                    {service}
                  </a>
                ))
                : '-'}
            </td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('prechecks.submitted')}</td>
            <td className="border px-4 py-2">{precheck?.createdAt ? format(new Date(precheck?.createdAt), 'MMM d, yyyy') : "-"}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.company')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genComp}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.address')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genAddr ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.directorName')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genDirName ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.directorEmail')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genDirEmail ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.directorAddress')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genDirAddr ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.federalTaxID')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genTaxID ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.description')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genDesc ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.pricing')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genPrice ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.urls')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genUrl ?? '-'}</td>
          </tr>
          {/* Only older submissions carry test logins; the form no longer asks for them. */}
          {precheck?.genInfo.genUser && (
            <tr>
              <td className="border px-4 py-2">{t('merchant.username')}</td>
              <td className="border px-4 py-2">{precheck.genInfo.genUser}</td>
            </tr>
          )}
          {precheck?.genInfo.genPass && (
            <tr>
              <td className="border px-4 py-2">{t('merchant.password')}</td>
              <td className="border px-4 py-2">{precheck.genInfo.genPass}</td>
            </tr>
          )}
          <tr>
            <td className="border px-4 py-2">{t('merchant.fullName')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genFullName ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.email')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genEmail ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.skype')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genSkype ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.phone')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genPhone ?? '-'}</td>
          </tr>
          <tr>
            <td className="border px-4 py-2">{t('merchant.referralSource')}</td>
            <td className="border px-4 py-2">{precheck?.genInfo.genRefSource ?? '-'}</td>
          </tr>
        </tbody>
      </table>

      {precheck?.srv.includes('creditCard') && (
        <>
          <h3 id="creditCard" className="text-md font-bold mb-2 mt-8">
            {t('prechecks.creditCardProcessing')}
          </h3>
          <table className="table-auto w-full text-sm my-2">
            <tbody>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.fulfillment')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpFulfill ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.callCenter')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpCallCtr ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.license')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpLic ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.licenseJurisdiction')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpLicJur ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.bankAcquirers')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpBankAcq ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.currentMCC')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpCurrMCC ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.averageTransactionValue')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpAvgTxVal ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.currentMonthlyVolume')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpCurrMonthVol ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.expectedMonthlyVolume')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpExpMonthVol ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.billingDescriptorLine1')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpDescLine1 ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.billingDescriptorLine2')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpDescLine2 ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.preferredGateway')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpPrefGateway ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.processingCurrenciesNeeded')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpProcCurr ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.settlementCurrenciesNeeded')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpSettleCurr ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.targetMarkets')}</td>
                <td className="border px-4 py-2">{precheck?.ccpInfo.ccpTargetMarkets ?? '-'}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {precheck?.srv.includes('affiliate') && (
        <>
          <h3 id="affiliate" className="text-md font-bold mb-2 mt-8">
            {t('prechecks.affiliatePayoutProgram')}
          </h3>

          <table className="table-auto w-full text-sm my-2">
            <tbody>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.companyRegistrationNumber')}</td>
                <td className="border px-4 py-2">{precheck?.apInfo.apRegNum ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.foreseenMainUsage')}</td>
                <td className="border px-4 py-2">{precheck?.apInfo.apUsage ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.projectedMonthlyTotalVolume')}</td>
                <td className="border px-4 py-2">{precheck?.apInfo.apProjVolume ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.projectedMonthlyTransactionCount')}</td>
                <td className="border px-4 py-2">{precheck?.apInfo.apProjCount ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.averageTransactionValue')}</td>
                <td className="border px-4 py-2">{precheck?.apInfo.apAvgTxValue ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.howManyCards')}</td>
                <td className="border px-4 py-2">{precheck?.apInfo.apCardCount ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.currenciesRequired')}</td>
                <td className="border px-4 py-2">{precheck?.apInfo.apCurrencies ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.selectedProducts')}</td>
                <td className="border px-4 py-2">
                  {precheck?.apInfo.apProduct?.length ? precheck.apInfo.apProduct.join(', ') : '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {precheck?.srv.includes('whitelabelGateway') && (
        <>
          <h3 id="whitelabelGateway" className="text-md font-bold mb-2 mt-8">
            {t('prechecks.whitelabelGateway')}
          </h3>

          <table className="table-auto w-full text-sm my-2">
            <tbody>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.expectedMonthlyProcessingVolume')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlExpMonthVol ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.expectedMonthlyTransactionCount')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlExpTxCount ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.averageTransactionValue')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlAvgTxVal ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.doYouHoldALicense')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlLicense ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.licenseJurisdiction')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlLicJur ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.neededGatewayFeatures')}</td>
                <td className="border px-4 py-2">
                  {precheck?.wlInfo.wlGatewayFeatures?.length
                    ? precheck.wlInfo.wlGatewayFeatures.join(', ')
                    : '-'}
                </td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.specifyOtherGatewayFeatures')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlOtherGateway ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.targetMarkets')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlTargetMarkets ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.neededBankAcquirers')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlBankAcquirers ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.neededAlternativeBillingSolutions')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlAltBilling ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.otherRemarks')}</td>
                <td className="border px-4 py-2">{precheck?.wlInfo.wlOtherRemarks ?? '-'}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {precheck?.srv.includes('clearingHouse') && (
        <>
          <h3 id="clearingHouse" className="text-md font-bold mb-2 mt-8">
            {t('prechecks.clearingHouse')}
          </h3>

          <table className="table-auto w-full text-sm my-2">
            <tbody>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.payeeName')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chPayee ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.clientName')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chClient ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.customerServiceEmail')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chCustEmail ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.marketingDescription')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chMktDesc ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.complaintHandling')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chCompHandling ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.refundPolicy')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chRefundPol ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.achProvider')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chAchProvider ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.customerServiceManager')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chAchName ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.managerPhone')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chAchPhone ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.managerEmail')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chAchEmail ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.highestTransactionAmount')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chHighTxAmt ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.averageTransactionAmount')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chAvgTxAmt ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.maxTransactionsPerDay')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chMaxTxPerDay ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.requestedMonthlyLimit')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chReqMonthLimit ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.recurringTransactionsPercentage')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chRecTxPerc ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.refundCapability')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chRefundCap ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.achCreditCapability')}</td>
                <td className="border px-4 py-2">{precheck?.chInfo.chAchCreditCap ?? '-'}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {precheck?.srv.includes('corporateBanking') && (
        <>
          <h3 id="corporateBanking" className="text-md font-bold mb-2 mt-8">
            {t('prechecks.corporateBanking')}
          </h3>

          <table className="table-auto w-full text-sm my-2">
            <tbody>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.businessAddress')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbBusinessAddr ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.areDirectorsUBOPep')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbPEP ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.mainClientsPartners')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbMainClients ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.fromCountry')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbFromCountry ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.currency')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbCurrency ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.monthlyVolume')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbMonthlyVol ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.numberOfPayments')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbNumPayments ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.incomingFundsActivity')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbIncomingFunds ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.toCountry')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbToCountry ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.outgoingFundsActivity')}</td>
                <td className="border px-4 py-2">{precheck?.cbInfo.cbOutgoingFunds ?? '-'}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {precheck?.srv.includes('incorporation') && (
        <>
          <h3 id="incorporation" className="text-md font-bold mb-2 mt-8">
            {t('prechecks.incorporation')}
          </h3>

          <table className="table-auto w-full text-sm my-2">
            <tbody>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.suggestedCompanyName')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incSuggestedName ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.officeInUSA')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incOfficeInUSA ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.currentMerchantName')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incCurrentMerchantName ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.existingAddress')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incExistingAddress ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.companyDescription')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incCompanyDescription ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.monthlyProcessingVolume')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incMonthlyVolume ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.averageTransactionValue')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incAvgTransactionValue ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.billingDescriptorLine1')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incBillingDescriptor1 ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.cityField')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incCityField ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.beneficialOwnerName')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incBOName1 ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.beneficialOwnerNationality')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incBONationality1 ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.beneficialOwnerAddress')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incBOAddress1 ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.beneficialOwnerShares')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incBOShares1 ?? '-'}</td>
              </tr>
              {precheck?.incInfo.incBOName2 && (
                <>
                  <tr>
                    <td className="border px-4 py-2">{t('prechecks.beneficialOwnerName2')}</td>
                    <td className="border px-4 py-2">{precheck?.incInfo.incBOName2 ?? '-'}</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">{t('prechecks.beneficialOwnerNationality2')}</td>
                    <td className="border px-4 py-2">{precheck?.incInfo.incBONationality2 ?? '-'}</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">{t('prechecks.beneficialOwnerAddress2')}</td>
                    <td className="border px-4 py-2">{precheck?.incInfo.incBOAddress2 ?? '-'}</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">{t('prechecks.beneficialOwnerShares2')}</td>
                    <td className="border px-4 py-2">{precheck?.incInfo.incBOShares2 ?? '-'}</td>
                  </tr>
                </>
              )}
              <tr>
                <td className="border px-4 py-2">{t('prechecks.directorName')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incDirectorName1 ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.directorNationality')}</td>
                <td className="border px-4 py-2">{precheck?.incInfo.incDirectorNationality1 ?? '-'}</td>
              </tr>
              {precheck?.incInfo.incDirectorName2 && (
                <>
                  <tr>
                    <td className="border px-4 py-2">{t('prechecks.directorName2')}</td>
                    <td className="border px-4 py-2">{precheck?.incInfo.incDirectorName2 ?? '-'}</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">{t('prechecks.directorNationality2')}</td>
                    <td className="border px-4 py-2">{precheck?.incInfo.incDirectorNationality2 ?? '-'}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </>
      )}

      {precheck?.srv.includes('alternativeBilling') && (
        <>
          <h3 id="alternativeBilling" className="text-md font-bold mb-2 mt-8">
            {t('prechecks.alternativeBilling')}
          </h3>

          <table className="table-auto w-full text-sm my-2">
            <tbody>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.selectedProducts')}</td>
                <td className="border px-4 py-2">
                  {precheck?.abInfo.abProduct?.length ? precheck.abInfo.abProduct.join(', ') : '-'}
                </td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.projectedMonthlyVolume')}</td>
                <td className="border px-4 py-2">{precheck?.abInfo.abProjectedVolume ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.projectedMonthlyNumberOfTransactions')}</td>
                <td className="border px-4 py-2">{precheck?.abInfo.abProjectedTxCount ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.lowestTransactionAmount')}</td>
                <td className="border px-4 py-2">{precheck?.abInfo.abLowestTxAmount ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.highestTransactionAmount')}</td>
                <td className="border px-4 py-2">{precheck?.abInfo.abHighestTxAmount ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.averageTransactionAmount')}</td>
                <td className="border px-4 py-2">{precheck?.abInfo.abAvgTxAmount ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.targetMarkets')}</td>
                <td className="border px-4 py-2">{precheck?.abInfo.abTargetMarkets ?? '-'}</td>
              </tr>
              <tr>
                <td className="border px-4 py-2">{t('prechecks.lastYearsSalesTurnoverInEUR')}</td>
                <td className="border px-4 py-2">{precheck?.abInfo.abSalesTurnover ?? '-'}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}
    </>
  );

  return (
    <SinglePrecheckView
      type="precheck"
      data={precheck}
      loading={loading}
      error={error}
      getTitle={getTitle}
      renderContent={renderContent}
    />
  );
};

export default SinglePrecheck;
